import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtINR, fmtDate, downloadBlob, API, resolveImage } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileDown, ArrowLeft, Printer, Share2, User, Store, Phone, Send } from "lucide-react";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [customer, setCustomer] = useState(null);

  // Recipient selector state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareType, setShareType] = useState("text"); // 'text' | 'pdf'

  useEffect(() => {
    api.get(`/orders/${id}`).then(r => setOrder(r.data));
    api.get("/products").then(r => setProducts(r.data));
  }, [id]);
  useEffect(() => {
    if (!order) return;
    api.get("/customers").then(r => setCustomer(r.data.find(c => c.id === order.customer_id)));
  }, [order]);

  if (!order) return <div className="text-slate-500 text-sm">Loading…</div>;

  const productMap = Object.fromEntries(products.map(p => [p.id, p]));

  function buildShareText() {
    const lines = [];
    lines.push(`*Omkar Milk Distributor*`);
    lines.push(`Invoice: *${order.invoice_no}*`);
    lines.push(`Date: ${fmtDate(order.order_date)}`);
    lines.push(`Bill To: ${order.customer_name}`);
    lines.push("");
    lines.push("Items:");
    order.items.forEach((it, i) => {
      lines.push(`${i+1}. ${it.product_name} — ${Number(it.quantity)} ${it.unit} × ₹${Number(it.rate).toFixed(2)} = ₹${Number(it.amount).toFixed(2)}`);
    });
    lines.push("");
    lines.push(`*Invoice Total: ₹${Number(order.total_amount).toFixed(2)}*`);
    if (customer) {
      if (customer.advance > 0) {
        lines.push(`*Remaining Balance (Advance): ₹${Number(customer.advance).toFixed(2)}*`);
      } else {
        lines.push(`*Remaining Balance (Outstanding): ₹${Number(customer.outstanding || 0).toFixed(2)}*`);
      }
    }
    if (order.remarks) { lines.push(""); lines.push(`Note: ${order.remarks}`); }
    lines.push("");
    lines.push("Thank you for your business!");
    return lines.join("\n");
  }

  function handleShareClick(type) {
    if (customer && customer.mobile_secondary) {
      setShareType(type);
      setShareModalOpen(true);
    } else {
      if (type === "text") executeShareText(customer?.mobile || "");
      else executeSharePdf(customer?.mobile || "");
    }
  }

  async function executeShareText(phoneInput) {
    const phoneRaw = (phoneInput || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const text = encodeURIComponent(buildShareText());
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setShareModalOpen(false);
  }

  async function executeSharePdf(phoneInput) {
    const phoneRaw = (phoneInput || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const token = localStorage.getItem("ddms_token");
    const pdfUrl = `${API}/export/invoice/${order.id}.pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    
    const fullText = `${buildShareText()}\n\n📄 *Download Invoice PDF:* ${pdfUrl}`;
    setShareModalOpen(false);
    
    try {
      const resp = await fetch(pdfUrl);
      const blob = await resp.blob();
      const file = new File([blob], `${order.invoice_no}.pdf`, { type: "application/pdf" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Invoice ${order.invoice_no}`,
          text: fullText,
        });
        return;
      }

      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${order.invoice_no}.pdf`;
      a.click();
      URL.revokeObjectURL(u);

      toast.info("Downloading PDF & opening WhatsApp...");
      const textParam = encodeURIComponent(fullText);
      const waUrl = phone ? `https://wa.me/${phone}?text=${textParam}` : `https://wa.me/?text=${textParam}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Share error:", err);
      const textParam = encodeURIComponent(fullText);
      const waUrl = phone ? `https://wa.me/${phone}?text=${textParam}` : `https://wa.me/?text=${textParam}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <div data-testid="order-detail">
      <PageHeader
        eyebrow={order.invoice_no}
        title={`Invoice — ${order.customer_name}`}
        description={`Order date: ${fmtDate(order.order_date)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/orders"><Button variant="outline"><ArrowLeft size={14} className="mr-1"/> Back</Button></Link>
            <Button
              variant="outline"
              onClick={() => handleShareClick("text")}
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="whatsapp-share-text-btn"
              title="Share order details on WhatsApp"
            >
              <Share2 size={14} className="mr-1"/>WhatsApp
            </Button>
            <Button
              onClick={() => handleShareClick("pdf")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="whatsapp-share-pdf-btn"
              title="Share PDF invoice via WhatsApp"
            >
              <Share2 size={14} className="mr-1"/>WhatsApp + PDF
            </Button>
            <Button className="bg-cobalt-500 hover:bg-cobalt-700 text-white" onClick={() => downloadBlob(`/export/invoice/${order.id}.pdf`, `${order.invoice_no}.pdf`)} data-testid="invoice-pdf-btn"><FileDown size={14} className="mr-1"/>Download PDF</Button>
          </div>
        }
      />

      <Card padding="p-0">
        <div className="p-6 border-b border-slate-200 flex justify-between items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Billed By</div>
            <div className="font-heading text-lg font-semibold mt-1">Omkar Milk Distributor</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mt-4">Billed To</div>
            <div className="font-heading text-xl font-semibold mt-2">{order.customer_name}</div>
            {order.remarks && <div className="text-sm text-slate-500 mt-1">{order.remarks}</div>}
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Total</div>
            <div className="font-heading font-mono text-3xl font-bold mt-2 text-cobalt-600">{fmtINR(order.total_amount)}</div>
          </div>
        </div>
        <table className="ddms-table">
          <thead><tr><th>#</th><th></th><th>Product</th><th>Unit</th><th className="num">Qty</th><th className="num">Crates Required</th><th className="num">Rate</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {order.items.map((it, i) => {
              const prod = productMap[it.product_id];
              const capacity = prod?.items_per_crate || 24;
              const crates = Math.ceil(Number(it.quantity) / capacity);
              return (
                <tr key={i}>
                  <td className="font-mono text-xs text-slate-500">{i+1}</td>
                  <td>
                    {prod?.image_url ? (
                      <img src={resolveImage(prod.image_url)} alt={it.product_name} className="w-10 h-10 rounded-md object-cover border border-slate-200"/>
                    ) : (
                      <div className="w-10 h-10 rounded-md border border-dashed border-slate-300 bg-slate-50"/>
                    )}
                  </td>
                  <td className="font-medium">{it.product_name}</td>
                  <td>{it.unit}</td>
                  <td className="num">{Number(it.quantity).toFixed(2)}</td>
                  <td className="num font-mono font-semibold text-emerald-700 bg-emerald-50/60 rounded px-2 py-1">
                    📦 {crates} {crates === 1 ? "Crate" : "Crates"}
                    <span className="text-[10px] text-slate-400 font-normal block">({capacity}/crate)</span>
                  </td>
                  <td className="num">{fmtINR(it.rate)}</td>
                  <td className="num font-semibold">{fmtINR(it.amount)}</td>
                </tr>
              );
            })}
            <tr className="bg-cobalt-50/50">
              <td colSpan={5} className="font-semibold text-xs text-emerald-800">
                Total Order Load: 📦 {order.items.reduce((acc, it) => {
                  const capacity = productMap[it.product_id]?.items_per_crate || 24;
                  return acc + Math.ceil(Number(it.quantity) / capacity);
                }, 0)} Crates
              </td>
              <td className="num font-semibold uppercase tracking-wider text-xs">Grand Total</td>
              <td colSpan={2} className="num font-bold text-cobalt-700 text-base">{fmtINR(order.total_amount)}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* WhatsApp Recipient Selection Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-heading font-semibold">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
                  <Share2 size={20} className="text-white" />
                </div>
                Share Invoice on WhatsApp
              </DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex items-center justify-between text-xs text-emerald-100 bg-white/10 backdrop-blur-md rounded-lg px-3 py-2 border border-white/15">
              <div>
                <span className="opacity-75">Invoice:</span> <strong className="text-white font-mono text-sm">{order?.invoice_no}</strong>
              </div>
              <div className="text-right">
                <span className="opacity-75">Total:</span> <strong className="text-white font-mono text-sm">₹{Number(order?.total_amount || 0).toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <div className="p-5 bg-slate-50/50">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Store size={14} className="text-cobalt-600" />
              <span>{customer?.business_name}</span>
              <span className="text-slate-400 font-normal">({customer?.customer_name})</span>
            </div>

            <div className="space-y-3">
              <div
                onClick={() => shareType === "text" ? executeShareText(customer?.mobile) : executeSharePdf(customer?.mobile)}
                className="group relative flex items-center justify-between p-4 bg-white hover:bg-emerald-50/60 border-2 border-emerald-500/30 hover:border-emerald-500 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">Owner (Primary)</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">Primary</span>
                    </div>
                    <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                      <Phone size={12} className="text-slate-400" />
                      {customer?.mobile || "N/A"}
                    </div>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                  <Send size={14} />
                </div>
              </div>

              {customer?.mobile_secondary && (
                <div
                  onClick={() => shareType === "text" ? executeShareText(customer?.mobile_secondary) : executeSharePdf(customer?.mobile_secondary)}
                  className="group relative flex items-center justify-between p-4 bg-white hover:bg-cobalt-50/60 border-2 border-slate-200 hover:border-cobalt-400 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-full bg-cobalt-100 text-cobalt-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                      <Store size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-sm">Cashier / Shop Manager</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">Secondary</span>
                      </div>
                      <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                        <Phone size={12} className="text-slate-400" />
                        {customer?.mobile_secondary}
                      </div>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-cobalt-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                    <Send size={14} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShareModalOpen(false)} className="rounded-lg text-xs font-medium">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
